import { Component, ErrorInfo, ReactNode } from 'react'
import { AlertTriangle, RefreshCw, Home } from 'lucide-react'

interface Props {
  children: ReactNode
  fallback?: ReactNode
}

interface State {
  hasError: boolean
  error: Error | null
  errorInfo: ErrorInfo | null
}

/**
 * Error Boundary component to catch and display React errors gracefully
 * Prevents blank screens by showing a user-friendly error page
 */
export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props)
    this.state = { hasError: false, error: null, errorInfo: null }
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null }
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo)
    this.setState({ errorInfo })
  }

  handleReload = () => {
    window.location.reload()
  }

  handleGoHome = () => {
    window.location.href = '/'
  }

  handleRetry = () => {
    // v35: a soft retry just remounts the same broken tree which is
    // about to throw the same error (typically `React #300` / max-update
    // loops). Force a hard reload so the user actually escapes the loop
    // instead of bouncing the same exception forever.
    if (this.state.error?.message?.includes('Maximum update depth') ||
        this.state.error?.message?.includes('Minified React error #300')) {
      window.location.reload()
      return
    }
    this.setState({ hasError: false, error: null, errorInfo: null })
  }

  render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback
      }

      return (
        <div className="min-h-screen bg-surface-950 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-surface-900 rounded-xl border border-surface-700 p-6 text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-status-critical/20 rounded-full">
                <AlertTriangle className="w-8 h-8 text-status-critical" />
              </div>
            </div>

            <h1 className="text-xl font-semibold text-surface-100 mb-2">
              Something went wrong
            </h1>

            <p className="text-surface-400 mb-6">
              An unexpected error occurred. This has been logged and our team will investigate.
            </p>

            {/* Error details - collapsed by default */}
            {this.state.error && (
              <details className="mb-6 text-left">
                <summary className="text-sm text-surface-500 cursor-pointer hover:text-surface-400">
                  Technical details
                </summary>
                <div className="mt-2 p-3 bg-surface-950 rounded-lg overflow-auto max-h-40">
                  <pre className="text-xs text-status-critical font-mono">
                    {this.state.error.message}
                  </pre>
                  {this.state.errorInfo?.componentStack && (
                    <pre className="text-xs text-surface-500 font-mono mt-2">
                      {this.state.errorInfo.componentStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div className="flex gap-3 justify-center">
              <button
                onClick={this.handleRetry}
                className="flex items-center gap-2 px-4 py-2 bg-primary-600 hover:bg-primary-500 text-white rounded-lg transition-colors"
              >
                <RefreshCw size={16} />
                Try Again
              </button>
              <button
                onClick={this.handleGoHome}
                className="flex items-center gap-2 px-4 py-2 bg-surface-700 hover:bg-surface-600 text-surface-200 rounded-lg transition-colors"
              >
                <Home size={16} />
                Go Home
              </button>
            </div>
          </div>
        </div>
      )
    }

    return this.props.children
  }
}

export default ErrorBoundary
